import { useEffect, useMemo, useState } from 'react';
import { ocrCaption, saveCaption, createLocJob, type LocJobCaptionInput } from '@/api/play';
import type { SourceShot } from '@/lib/playScreenshots';
import { playLocaleName } from '@/lib/playLocales';
import { Modal } from '../Modal';
import { Button } from '../Button';

/** Tasks the worker drains per minute — keep in sync with TASK_BATCH in
 *  api/cloudflare/src/lib/play-loc-runner.ts. Only used for the ETA. */
const TASKS_PER_MIN = 4;

interface CaptionRow extends SourceShot {
  caption: string;
  loading: boolean;
  error?: string;
}

interface BulkLocalizeModalProps {
  isOpen: boolean;
  appId: number;
  sourceLang: string;
  /** Board locales, source excluded by the caller. */
  langs: string[];
  emptyLangs: string[];
  sourceItems: SourceShot[];
  sourceText: { title: string; shortDescription: string; fullDescription: string };
  /** Per-locale existing video, carried through so a regenerated draft keeps it. */
  videos: Record<string, string>;
  onClose: () => void;
  onStarted: () => void;
}

export function BulkLocalizeModal({
  isOpen, appId, sourceLang, langs, emptyLangs, sourceItems, sourceText, videos, onClose, onStarted,
}: BulkLocalizeModalProps) {
  const [step, setStep] = useState<'select' | 'captions'>('select');
  const [selected, setSelected] = useState<string[]>([]);
  const [doText, setDoText] = useState(true);
  const [doScreenshots, setDoScreenshots] = useState(true);
  const [rows, setRows] = useState<CaptionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const hasScreenshots = sourceItems.length > 0;
  const sourceEmpty = !sourceText.title && !sourceText.shortDescription && !sourceText.fullDescription;

  useEffect(() => {
    if (!isOpen) return;
    setStep('select');
    setError(null);
    setSelected(emptyLangs.length > 0 ? emptyLangs : langs);
    setDoText(!sourceEmpty);
    setDoScreenshots(hasScreenshots);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const taskCount = selected.length * ((doText ? 1 : 0) + (doScreenshots ? sourceItems.length : 0));
  const eta = Math.max(1, Math.ceil(taskCount / TASKS_PER_MIN));

  const toggle = (lang: string) =>
    setSelected((p) => p.includes(lang) ? p.filter((l) => l !== lang) : [...p, lang]);

  // Step 2 exists to gate the OCR: one English caption feeds every locale, so a
  // misread here would multiply into a paid render per locale.
  const loadCaptions = async () => {
    setStep('captions');
    setRows(sourceItems.map((it) => ({ ...it, caption: '', loading: true })));
    for (const it of sourceItems) {
      try {
        const { caption } = await ocrCaption(appId, { imageKey: it.key, imageUrl: it.url, sourceLang });
        setRows((prev) => prev.map((r) => r.key === it.key ? { ...r, caption, loading: false } : r));
      } catch (e: any) {
        setRows((prev) => prev.map((r) => r.key === it.key
          ? { ...r, loading: false, error: e?.message ?? 'OCR failed' } : r));
      }
    }
  };

  const next = () => {
    setError(null);
    if (selected.length === 0) return setError('Pick at least one locale');
    if (!doText && !doScreenshots) return setError('Pick texts, screenshots, or both');
    if (doScreenshots) loadCaptions();
    else start([]);
  };

  const start = async (captions: LocJobCaptionInput[]) => {
    setStarting(true);
    setError(null);
    try {
      // Persist caption fixes so the next run starts from the corrected text.
      await Promise.all(
        captions
          .filter((cap) => cap.imageKey)
          .map((cap) => saveCaption(appId, { imageKey: cap.imageKey, sourceLang, caption: cap.caption })
            .catch(() => { /* the job already carries the text; caching is a bonus */ })),
      );
      await createLocJob(appId, {
        sourceLang, langs: selected, doText, doScreenshots,
        ...sourceText, videos, captions,
      });
      onStarted();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to start the run');
    } finally {
      setStarting(false);
    }
  };

  const captionsLoading = rows.some((r) => r.loading);

  const localeGrid = useMemo(() => langs.map((lang) => (
    <label
      key={lang}
      title={playLocaleName(lang) ?? undefined}
      className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs border cursor-pointer ${
        selected.includes(lang) ? 'border-primary text-content' : 'border-border text-content-muted'
      }`}
    >
      <input type="checkbox" checked={selected.includes(lang)} onChange={() => toggle(lang)} />
      {lang}
      {emptyLangs.includes(lang) && <span className="text-content-muted">·empty</span>}
    </label>
  )), [langs, selected, emptyLangs]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Localize all" size="lg">
      {step === 'select' && (
        <div className="space-y-4">
          <p className="text-xs text-content-muted">
            Runs in the background from <b>{sourceLang}</b> — you can close this tab and come back.
            Everything lands in drafts for review; nothing is sent to Play.
          </p>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-content-muted">Locales</span>
              <button className="text-xs text-primary" onClick={() => setSelected(langs)}>all</button>
              <button className="text-xs text-primary" onClick={() => setSelected(emptyLangs)}>only empty</button>
              <button className="text-xs text-content-muted" onClick={() => setSelected([])}>none</button>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">{localeGrid}</div>
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={doText} disabled={sourceEmpty}
                onChange={(e) => setDoText(e.target.checked)} />
              Texts
              {sourceEmpty && <span className="text-xs text-red-400">source is empty</span>}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={doScreenshots} disabled={!hasScreenshots}
                onChange={(e) => setDoScreenshots(e.target.checked)} />
              Screenshots
              {!hasScreenshots && <span className="text-xs text-content-muted">source has none</span>}
            </label>
          </div>

          <p className="text-xs text-content-muted">
            {selected.length} locale(s) × ({doText ? 'text' : ''}{doText && doScreenshots ? ' + ' : ''}
            {doScreenshots ? `${sourceItems.length} screenshots` : ''}) = <b>{taskCount}</b> tasks, ~{eta} min
          </p>

          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button disabled={taskCount === 0 || starting} loading={starting} onClick={next}>
              {doScreenshots ? 'Next: check captions' : 'Start'}
            </Button>
          </div>
        </div>
      )}

      {step === 'captions' && (
        <div className="space-y-3">
          <p className="text-xs text-content-muted">
            These English captions feed every selected locale — fix them here and the whole run uses the fix.
            An empty caption leaves that screenshot unchanged (no render).
          </p>
          <div className="space-y-2 max-h-[24rem] overflow-y-auto">
            {rows.map((r) => (
              <div key={r.key} className="flex gap-3 items-center border border-border rounded-md p-2">
                <img src={r.url} alt="" className="h-20 rounded border border-border object-cover" />
                <div className="flex-1">
                  <input
                    className="w-full px-2 py-1 rounded bg-background border border-border text-content text-sm"
                    value={r.caption}
                    placeholder={r.loading ? 'Reading…' : 'No caption detected'}
                    onChange={(e) => setRows((prev) => prev.map((x) => x.key === r.key ? { ...x, caption: e.target.value } : x))}
                  />
                  {r.error && <span className="text-xs text-red-400">{r.error}</span>}
                </div>
              </div>
            ))}
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setStep('select')}>Back</Button>
            <Button
              disabled={captionsLoading || starting}
              loading={starting}
              onClick={() => start(rows.map((r, i) => ({
                imageKey: r.key, imageUrl: r.url, caption: r.caption, position: i,
              })))}
            >
              Start {taskCount} tasks
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
